import React from 'react';
import { Card } from '@/components/ui/card';
import { Phone, TrendingUp, AlertTriangle } from 'lucide-react';

interface PhoneSequence {
  baseNumber: string;
  sequence: string[];
  timeGaps: string[];
  confidence: number;
  status: 'active' | 'blocked' | 'investigating';
}

export const SequentialPhonePattern: React.FC = () => {
  const sequences: PhoneSequence[] = [
    {
      baseNumber: '2262433171',
      sequence: ['2262433171', '2262433172', '2262433173', '2262433174'],
      timeGaps: ['1m 23s', '58s', '1m 12s'],
      confidence: 98.5,
      status: 'active'
    },
    {
      baseNumber: '5551234567',
      sequence: ['5551234567', '5551234568', '5551234569'],
      timeGaps: ['2m 15s', '1m 45s'],
      confidence: 92.3,
      status: 'blocked'
    },
    {
      baseNumber: '8885556677',
      sequence: ['8885556677', '8885556678', '8885556679', '8885556680', '8885556681'],
      timeGaps: ['45s', '1m 2s', '38s', '1m 28s'],
      confidence: 96.8,
      status: 'investigating'
    }
  ];

  const stats = {
    activeSequences: sequences.filter(s => s.status === 'active').length,
    averageConfidence: sequences.reduce((sum, s) => sum + s.confidence, 0) / sequences.length,
    maxLength: Math.max(...sequences.map(s => s.sequence.length)),
    totalDetected: sequences.reduce((sum, s) => sum + s.sequence.length, 0)
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-red-100 text-red-800';
      case 'blocked':
        return 'bg-gray-100 text-gray-800';
      case 'investigating':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'blocked':
        return <div className="w-4 h-4 rounded-full bg-gray-500" />;
      case 'investigating':
        return <TrendingUp className="w-4 h-4 text-yellow-500" />;
      default:
        return <Phone className="w-4 h-4" />;
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Phone className="w-6 h-6 text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Sequential Phone Detection</h3>
            <p className="text-sm text-muted-foreground">Automated phone number pattern analysis</p>
          </div>
        </div>
        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
          {stats.activeSequences} Active
        </span>
      </div>

      <div className="space-y-4 mb-6">
        {sequences.map((sequence, index) => (
          <div key={index} className={`p-4 rounded-lg border ${
            sequence.status === 'active' ? 'bg-red-50 border-red-200' :
            sequence.status === 'blocked' ? 'bg-gray-50 border-gray-200' :
            'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {getStatusIcon(sequence.status)}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(sequence.status)}`}>
                  {sequence.status.toUpperCase()}
                </span>
              </div>
              <div className="text-sm font-medium text-foreground">
                {sequence.confidence}% confidence
              </div>
            </div>
            
            <div className="font-mono text-sm mb-2">
              {sequence.sequence.map((phone, phoneIndex) => (
                <span key={phoneIndex}>
                  <span className={phoneIndex === 0 ? 'text-foreground' : 'text-red-600 font-semibold'}>
                    {phone}
                  </span>
                  {phoneIndex < sequence.sequence.length - 1 && (
                    <>
                      <span className="text-muted-foreground mx-2">→</span>
                    </>
                  )}
                </span>
              ))}
            </div>
            
            {sequence.timeGaps.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Time gaps: {sequence.timeGaps.join(' → ')} (avg: {
                  sequence.timeGaps.reduce((sum, gap) => {
                    const seconds = gap.includes('m') ? 
                      parseInt(gap) * 60 + (gap.includes('s') ? parseInt(gap.split('m ')[1]) : 0) :
                      parseInt(gap);
                    return sum + seconds;
                  }, 0) / sequence.timeGaps.length
                }s)
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-4 gap-4 pt-4 border-t">
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">{stats.activeSequences}</div>
          <div className="text-xs text-muted-foreground">Active Sequences</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">{stats.averageConfidence.toFixed(1)}%</div>
          <div className="text-xs text-muted-foreground">Avg Confidence</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">{stats.maxLength}</div>
          <div className="text-xs text-muted-foreground">Max Length</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">{stats.totalDetected}</div>
          <div className="text-xs text-muted-foreground">Total Detected</div>
        </div>
      </div>
    </Card>
  );
};